import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, IconButton, Paper, Chip
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { API_URL } from '../../config';

const GREEN  = '#1b4332';
const TEAL   = '#0e7490';

const HDR_SX = {
  fontWeight: 700,
  color: '#fff',
  fontSize: '0.68rem',
  textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${TEAL} 0%, #0891b2 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)',
  py: 1,
  px: 0.7,
  whiteSpace: 'nowrap',
  textAlign: 'center',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

const CELL_SX = { py: 0.7, px: 0.7, fontSize: '0.73rem', textAlign: 'center', whiteSpace: 'nowrap' };

function fmtNum(v, d = 3) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

function fmtDate(v) {
  if (!v) return '—';
  return String(v).substring(0, 10);
}

function isGrandTotal(row) {
  return String(row.CLIENT || '').trim().toUpperCase().startsWith('GRAND TOTAL');
}

// ─── Status chip ─────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const s = String(status || '').toLowerCase();
  const color = s.includes('complet') ? 'success'
    : s.includes('under') || s.includes('load') ? 'warning'
    : s.includes('sail') ? 'info'
    : 'default';
  return <Chip label={status || '—'} color={color} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />;
}

// ─── Detail Table ─────────────────────────────────────────────────────────────
function DetailTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;

  const cols = [
    { label: 'CONTRACT DATE', key: 'CONTRACT_DATE',    fmt: fmtDate },
    { label: 'PURCHASE CONTRACT', key: 'PURCHASE_CONTRACT' },
    { label: 'ROUGIER REF',  key: 'ROUGIER_REFERENCE' },
    { label: 'CLIENT',       key: 'CLIENT' },
    { label: 'DESTINATION',  key: 'DESTINATION' },
    { label: 'QUALITY',      key: 'QUALITY' },
    { label: 'INCOTERM',     key: 'INCOTERM' },
    { label: 'CONTAINER NO', key: 'CONTAINER_NO' },
    { label: 'SUB ORDER',    key: 'SUBORDERNO' },
    { label: 'STATUS',       key: 'ORDER_STATUS',     isStatus: false },
    { label: 'LINER',        key: 'LINER' },
    { label: 'BOOKING NO',   key: 'BOOKING_NO' },
    { label: 'BL NUMBER',    key: 'BL_NUMBER' },
    { label: 'VESSEL',       key: 'VESSEL_NAME' },
    { label: 'ETD',          key: 'ETD',               fmt: fmtDate },
    { label: 'GATE IN',      key: 'GATE_IN',           fmt: fmtDate },
    { label: 'INVOICE NO',   key: 'INVOICE_NUMBER' },
    { label: 'INVOICE DATE', key: 'INVOICE_DATE',      fmt: fmtDate },
    { label: 'OBL NO',       key: 'OBL_NO' },
    { label: 'CBM (CONTAINER)', key: 'VOLUME_CBM',        fmt: v => fmtNum(v, 3) },
    { label: 'ORDER CBM',      key: 'ORDER_TOTAL_CBM',    fmt: v => fmtNum(v, 3) },
    { label: 'ORDER AMOUNT',   key: 'ORDER_TOTAL_AMOUNT', fmt: v => fmtNum(v, 2) },
    { label: 'CURRENCY',       key: 'CURRENCY' },
    { label: 'MONTH',        key: 'DELIVERY_MONTH' },
  ];

  return (
    <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 2200 }}>
        <TableHead>
          <TableRow>
            {cols.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#f0fdfa',
              '&:hover': { backgroundColor: '#ccfbf1' },
            }}>
              {cols.map(col => {
                if (col.isStatus) {
                  return (
                    <TableCell key={col.label} sx={CELL_SX}>
                      <StatusChip status={row[col.key]} />
                    </TableCell>
                  );
                }
                const val = col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                return <TableCell key={col.label} sx={CELL_SX}>{val}</TableCell>;
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Summary Table ────────────────────────────────────────────────────────────
function SummaryTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;

  const cols = [
    { label: 'CLIENT',          key: 'CLIENT' },
    { label: 'DESTINATION',     key: 'DESTINATION' },
    { label: 'QUALITY',         key: 'QUALITY' },
    { label: 'CONTAINERS',      key: 'CONTAINER_COUNT', fmt: v => fmtNum(v, 0) },
    { label: 'ORDERS',          key: 'ORDER_COUNT',     fmt: v => fmtNum(v, 0) },
    { label: 'TOTAL CBM',       key: 'TOTAL_CBM',       fmt: v => fmtNum(v, 3) },
    { label: 'TOTAL AMOUNT',    key: 'TOTAL_AMOUNT',    fmt: v => fmtNum(v, 2) },
    { label: 'CURRENCY',        key: 'CURRENCY' },
  ];

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 800 }}>
        <TableHead>
          <TableRow>
            {cols.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#164e63' : (i % 2 === 0 ? '#fff' : '#f0fdfa'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                {cols.map(col => {
                  const val = col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                  return (
                    <TableCell key={col.label} sx={{
                      ...CELL_SX,
                      fontWeight: grand ? 700 : 500,
                      color: grand ? '#ffffff' : 'inherit',
                    }}>
                      {val}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Stat chip ────────────────────────────────────────────────────────────────
function StatChip({ label, value, color = TEAL }) {
  return (
    <Paper elevation={0} sx={{
      px: 2, py: 1.5, borderRadius: 2,
      border: `1.5px solid ${color}30`,
      background: `${color}08`,
      textAlign: 'center',
      minWidth: 120,
    }}>
      <Typography sx={{ fontSize: '0.68rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color }}>
        {value}
      </Typography>
    </Paper>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────
const TABS = ['DETAIL', 'SUMMARY BY CLIENT'];

const ShippingScheduleView = ({ onBackClick }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate,   setToDate]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [data,     setData]     = useState(null);
  const [tab,      setTab]      = useState(0);

  const fetchData = async () => {
    if (!fromDate || !toDate) { setError('Please select both From and To invoice dates'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/shippingschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_date: fromDate.format('YYYY-MM-DD'),
          to_date:   toDate.format('YYYY-MM-DD'),
        }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data || apiData.data.every(rs => rs.length === 0)) {
        setError('No data found for the selected date range.');
        return;
      }
      setData(apiData.data);
      setTab(0);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!data || !data[0]?.length) return;

    const rows = data[0];
    const wb   = new ExcelJS.Workbook();
    wb.creator = 'Star Ply ERP';
    wb.created = new Date();

    const ws = wb.addWorksheet('Shipping Schedule', {
      views: [{ state: 'frozen', ySplit: 1 }],
    });

    // ── Column definitions (matches Rougier layout exactly) ──────────────────
    ws.columns = [
      { key: 'A', header: 'Date',               width: 12 },
      { key: 'B', header: 'Purchase contract',  width: 17 },
      { key: 'C', header: 'Client',             width: 24 },
      { key: 'D', header: 'Country',            width: 16 },
      { key: 'E', header: 'Volume cbm',         width: 11 },
      { key: 'F', header: 'Status',             width: 40 },
      { key: 'G', header: 'Destination',        width: 18 },
      { key: 'H', header: 'LINER',              width: 12 },
      { key: 'I', header: 'Booking/BL number',  width: 22 },
      { key: 'J', header: 'Container Number',   width: 18 },
      { key: 'K', header: 'Vessel Details',     width: 28 },
      { key: 'L', header: 'ETD',                width: 12 },
      { key: 'M', header: 'Rougier reference',  width: 16 },
      { key: 'N', header: 'MONTH',              width: 10 },
      { key: 'O', header: 'Quality',            width: 12 },
      { key: 'P', header: 'AMOUNT (EURO)',      width: 16 },
      { key: 'Q', header: 'Incoterm',           width: 9  },
    ];

    // ── Header row styling ────────────────────────────────────────────────────
    const headerRow = ws.getRow(1);
    headerRow.height = 22;
    headerRow.eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10, name: 'Calibri' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F6B3A' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin' }, bottom: { style: 'thin' },
        left: { style: 'thin' }, right: { style: 'thin' },
      };
    });

    // ── Group rows by PURCHASE_CONTRACT ──────────────────────────────────────
    // Build groups: each order = multiple container rows
    const groups = [];
    let currentGroup = null;
    for (const row of rows) {
      if (!currentGroup || row.PURCHASE_CONTRACT !== currentGroup.key) {
        currentGroup = { key: row.PURCHASE_CONTRACT, orderRow: row, containers: [] };
        groups.push(currentGroup);
      }
      currentGroup.containers.push(row);
    }

    // ── Helper styles ─────────────────────────────────────────────────────────
    const thinBorder = {
      top: { style: 'thin', color: { argb: 'FFB0C4B0' } },
      bottom: { style: 'thin', color: { argb: 'FFB0C4B0' } },
      left: { style: 'thin', color: { argb: 'FFB0C4B0' } },
      right: { style: 'thin', color: { argb: 'FFB0C4B0' } },
    };
    const outerBorder = {
      top: { style: 'medium', color: { argb: 'FF1F6B3A' } },
      bottom: { style: 'medium', color: { argb: 'FF1F6B3A' } },
      left: { style: 'medium', color: { argb: 'FF1F6B3A' } },
      right: { style: 'medium', color: { argb: 'FF1F6B3A' } },
    };

    const cellStyle = (fill = 'FFFFFFFF') => ({
      font: { size: 10, name: 'Calibri' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } },
      alignment: { vertical: 'middle', horizontal: 'center', wrapText: false },
      border: thinBorder,
    });

    // Alternating row fills
    const FILL_ODD  = 'FFFFFFFF';
    const FILL_EVEN = 'FFE8F5E9'; // light green tint

    let excelRow = 2; // start after header
    let groupIdx = 0;

    for (const group of groups) {
      const { orderRow, containers } = group;
      const rowCount   = containers.length;
      const startRow   = excelRow;
      const endRow     = excelRow + rowCount - 1;
      const fill       = groupIdx % 2 === 0 ? FILL_ODD : FILL_EVEN;
      groupIdx++;

      // ── Write each container row ──────────────────────────────────────────
      containers.forEach((ctr, ci) => {
        const r = ws.getRow(excelRow);
        r.height = 18;

        // Order-level columns (A-G, M-Q) — only set value on first row, rest stay blank for merge
        if (ci === 0) {
          // A: Contract Date
          const cell_A = r.getCell(1);
          cell_A.value = orderRow.CONTRACT_DATE ? new Date(orderRow.CONTRACT_DATE) : null;
          if (cell_A.value) cell_A.numFmt = 'dd-mm-yyyy';
          // B: Rougier Reference (CT3500) in Purchase contract column — closest match
          r.getCell(2).value = orderRow.ROUGIER_REFERENCE;
          // C: Client
          r.getCell(3).value = orderRow.CLIENT;
          // D: Country (may be null — leave blank)
          r.getCell(4).value = orderRow.COUNTRY || '';
          // E: Volume CBM (total for order — from SP's ORDER_TOTAL_CBM)
          r.getCell(5).value = parseFloat((orderRow.ORDER_TOTAL_CBM || 0).toFixed(3));
          r.getCell(5).numFmt = '#,##0.000';
          // F: Order-level status text (e.g. "4 Ctrs already sailed")
          r.getCell(6).value = orderRow.ORDER_STATUS || orderRow.CONTAINER_STATUS || '';
          // G: Destination
          r.getCell(7).value = orderRow.DESTINATION || '';
        } else {
          // Continuation rows — F (status) stays blank, it's merged from first row
        }

        // H-L: Container-level columns
        r.getCell(8).value  = ctr.LINER          || '';  // H: LINER
        r.getCell(9).value  = ctr.BOOKING_NO      || '';  // I: Booking/BL number
        r.getCell(10).value = ctr.CONTAINER_NO    || '';  // J: Container Number
        r.getCell(11).value = ctr.VESSEL_NAME     || '';  // K: Vessel Details
        // L: ETD
        if (ctr.ETD) {
          const etdCell = r.getCell(12);
          etdCell.value = new Date(ctr.ETD);
          etdCell.numFmt = 'dd-mm-yyyy';
        } else {
          r.getCell(12).value = '';
        }

        if (ci === 0) {
          // M-Q: Order-level (only on first row)
          r.getCell(13).value = orderRow.ROUGIER_REFERENCE || '';   // M: Rougier reference (same as col B)
          r.getCell(14).value = orderRow.DELIVERY_MONTH    || '';   // N
          r.getCell(15).value = orderRow.QUALITY           || '';   // O
          r.getCell(16).value = parseFloat((orderRow.ORDER_TOTAL_AMOUNT || 0).toFixed(2));
          r.getCell(16).numFmt = '#,##0.00';                         // P
          r.getCell(17).value = orderRow.INCOTERM           || '';   // Q
        }

        // Apply cell styles
        for (let col = 1; col <= 17; col++) {
          const cell = r.getCell(col);
          Object.assign(cell, cellStyle(fill));
        }

        r.commit();
        excelRow++;
      });

      // ── Merge order-level columns if multiple container rows ───────────────
      if (rowCount > 1) {
        const colNumToLetter = n => {
          let s = '';
          while (n > 0) { s = String.fromCharCode(64 + (n % 26 || 26)) + s; n = Math.floor((n - 1) / 26); }
          return s;
        };
        const mergeCols = [1, 2, 3, 4, 5, 6, 7, 13, 14, 15, 16, 17]; // A,B,C,D,E,F,G,M,N,O,P,Q
        for (const col of mergeCols) {
          const letter = colNumToLetter(col);
          ws.mergeCells(`${letter}${startRow}:${letter}${endRow}`);
          const mergedCell = ws.getCell(startRow, col);
          mergedCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          mergedCell.border = outerBorder;
        }
      }

      // ── Thick outer border around the whole order group ───────────────────
      for (let r2 = startRow; r2 <= endRow; r2++) {
        // Left border of col A (col=1)
        const leftCell = ws.getCell(r2, 1);
        leftCell.border = { ...leftCell.border, left: { style: 'medium', color: { argb: 'FF1F6B3A' } } };
        // Right border of col Q (col=17)
        const rightCell = ws.getCell(r2, 17);
        rightCell.border = { ...rightCell.border, right: { style: 'medium', color: { argb: 'FF1F6B3A' } } };
      }
      // Top border of first row
      for (let col = 1; col <= 17; col++) {
        const cell = ws.getCell(startRow, col);
        cell.border = { ...cell.border, top: { style: 'medium', color: { argb: 'FF1F6B3A' } } };
      }
      // Bottom border of last row
      for (let col = 1; col <= 17; col++) {
        const cell = ws.getCell(endRow, col);
        cell.border = { ...cell.border, bottom: { style: 'medium', color: { argb: 'FF1F6B3A' } } };
      }
    }

    // ── Summary sheet ─────────────────────────────────────────────────────────
    if (data[1]?.length) {
      const ws2 = wb.addWorksheet('Summary by Client');
      ws2.columns = [
        { header: 'Client',          width: 35 },
        { header: 'Destination',     width: 22 },
        { header: 'Quality',         width: 14 },
        { header: 'Containers',      width: 12 },
        { header: 'Orders',          width: 10 },
        { header: 'Total CBM',       width: 14 },
        { header: 'Total Amount',    width: 16 },
        { header: 'Currency',        width: 10 },
      ];
      const hdr2 = ws2.getRow(1);
      hdr2.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F6B3A' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = thinBorder;
      });
      data[1].forEach((row, i) => {
        const r = ws2.addRow([
          row.CLIENT, row.DESTINATION, row.QUALITY,
          row.CONTAINER_COUNT, row.ORDER_COUNT,
          parseFloat(row.TOTAL_CBM  || 0),
          parseFloat(row.TOTAL_AMOUNT || 0),
          row.CURRENCY,
        ]);
        r.getCell(6).numFmt = '#,##0.000';
        r.getCell(7).numFmt = '#,##0.00';
        r.eachCell(cell => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? FILL_ODD : FILL_EVEN } };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          cell.font = { size: 10, name: 'Calibri' };
          cell.border = thinBorder;
        });
      });
    }

    // ── Save file ─────────────────────────────────────────────────────────────
    const buffer = await wb.xlsx.writeBuffer();
    const fd = fromDate.format('YYYY-MM-DD');
    const td = toDate.format('YYYY-MM-DD');
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `ShippingSchedule_${fd}_to_${td}.xlsx`);
  };

  // Quick stats from summary
  const totalContainers = data?.[1]?.reduce((s, r) => s + (parseInt(r.CONTAINER_COUNT) || 0), 0);
  const totalCBM        = data?.[1]?.reduce((s, r) => s + (parseFloat(r.TOTAL_CBM) || 0), 0);
  const totalAmount     = data?.[1]?.reduce((s, r) => s + (parseFloat(r.TOTAL_AMOUNT) || 0), 0);
  const totalOrders     = data?.[0] ? new Set(data[0].map(r => r.PURCHASE_CONTRACT)).size : 0;

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>
      {/* Header card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(14,116,144,0.1)', border: '1px solid #cffafe' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: TEAL }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${GREEN} 0%, #155e75 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <LocalShippingIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>Shipping Schedule Report</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>Container-level shipping details filtered by contract date</Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker
                label="Contract From Date"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <DatePicker
                label="Contract To Date"
                value={toDate}
                onChange={setToDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <Button
                variant="contained" size="large" startIcon={<SearchIcon />}
                onClick={fetchData} disabled={loading}
                sx={{
                  minWidth: 140,
                  backgroundColor: TEAL,
                  '&:hover': { backgroundColor: '#164e63' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Search
              </Button>
            </Box>
          </LocalizationProvider>

          {/* Quick stats */}
          {data && (
            <Box display="flex" flexWrap="wrap" gap={1.5} mt={2}>
              <StatChip label="Orders"        value={totalOrders}                       color={GREEN} />
              <StatChip label="Containers"    value={totalContainers}                   color={TEAL} />
              <StatChip label="Total CBM"     value={fmtNum(totalCBM, 3)}               color={TEAL} />
              <StatChip label="Total Amount"  value={`€ ${fmtNum(totalAmount, 2)}`}     color="#b45309" />
            </Box>
          )}
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: TEAL }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #cffafe' }}>
          <Box sx={{
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2, pt: 1,
          }}>
            <Tabs
              value={tab} onChange={(_, v) => setTab(v)}
              textColor="inherit" variant="scrollable" scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' },
                '& .Mui-selected': { color: TEAL },
                '& .MuiTabs-indicator': { backgroundColor: TEAL },
              }}
            >
              {TABS.map((t, i) => (
                <Tab key={i} label={
                  i === 0 ? `${t} (${data[0]?.length || 0} rows)` : t
                } />
              ))}
            </Tabs>
            <Button
              size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}
            >
              Excel
            </Button>
          </Box>

          {tab === 0 && <DetailTable  rows={data[0]} />}
          {tab === 1 && <SummaryTable rows={data[1]} />}
        </Card>
      )}
    </Container>
  );
};

export default ShippingScheduleView;
